import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { SparkActivityFilters, SparkActivityStatus } from '../types/spark-activity';

const STORAGE_KEY = 'sparkadmin_spark_activity_filters';

const DEFAULT_FILTERS: SparkActivityFilters = {
  search: '',
  domainId: null,
  skillId: null,
  status: null,
  batchId: null,
};

function parseFiltersFromURL(searchParams: URLSearchParams): Partial<SparkActivityFilters> {
  const filters: Partial<SparkActivityFilters> = {};

  const search = searchParams.get('search');
  if (search) filters.search = search;

  const status = searchParams.get('status');
  if (status && ['draft', 'published', 'unpublished', 'archived'].includes(status)) {
    filters.status = status as SparkActivityStatus;
  }

  const domainId = searchParams.get('domainId');
  if (domainId) {
    const parsed = parseInt(domainId, 10);
    if (!isNaN(parsed)) filters.domainId = parsed;
  }

  const skillId = searchParams.get('skillId');
  if (skillId) {
    const parsed = parseInt(skillId, 10);
    if (!isNaN(parsed)) filters.skillId = parsed;
  }

  const batchId = searchParams.get('batchId');
  if (batchId) {
    const parsed = parseInt(batchId, 10);
    if (!isNaN(parsed)) filters.batchId = parsed;
  }

  return filters;
}

function loadFiltersFromStorage(): Partial<SparkActivityFilters> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function saveFiltersToStorage(filters: SparkActivityFilters): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // ignore
  }
}

function filtersToSearchParams(filters: SparkActivityFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.status) params.set('status', filters.status);
  if (filters.domainId) params.set('domainId', String(filters.domainId));
  if (filters.skillId) params.set('skillId', String(filters.skillId));
  if (filters.batchId) params.set('batchId', String(filters.batchId));
  return params;
}

export function useSparkActivityFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<SparkActivityFilters>(() => ({
    ...DEFAULT_FILTERS,
    ...loadFiltersFromStorage(),
    ...parseFiltersFromURL(searchParams),
  }));

  const updateFilter = useCallback(
    <K extends keyof SparkActivityFilters>(key: K, value: SparkActivityFilters[K]) => {
      setFilters((prev) => {
        const updated = { ...prev, [key]: value };
        const params = filtersToSearchParams(updated);
        setSearchParams(params, { replace: true });
        saveFiltersToStorage(updated);
        return updated;
      });
    },
    [setSearchParams]
  );

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setSearchParams({}, { replace: true });
    saveFiltersToStorage(DEFAULT_FILTERS);
  }, [setSearchParams]);

  return { filters, updateFilter, clearFilters };
}
